import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    Skeleton,
    Typography,
} from '@mui/material'
import {
    ArrowBack,
    CalendarMonth,
    Person,
    AccountBalance,
    Category as CategoryIcon,
    Star,
    StarBorder,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import { api } from '../../../services/Main/Api'
import Masks from '../../../components/Shared/Masks'

const STATUS_MAP = {
    pending: { label: 'Pendente', color: '#F59E0B' },
    paid: { label: 'Pago', color: '#22C55E' },
    overdue: { label: 'Vencido', color: '#EF4444' },
    canceled: { label: 'Cancelado', color: '#94A3B8' },
}

const PaymentsDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const [payment, setPayment] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        api.get(`cashflow/payments/${id}/`)
            .then((res) => setPayment(res.data))
            .catch(() => enqueueSnackbar('Erro ao carregar pagamento.', { variant: 'error' }))
            .finally(() => setLoading(false))
    }, [id])

    const handleToggleFavorite = () => {
        api.patch(`cashflow/payments/${id}/`, { is_favorite: !payment.is_favorite })
            .then((res) => setPayment(res.data))
            .catch(() => {})
    }

    const handleCancel = () => {
        api.patch(`cashflow/payments/${id}/`, { status: 'canceled' })
            .then((res) => {
                setPayment(res.data)
                enqueueSnackbar('Pagamento cancelado.', { variant: 'success' })
            })
            .catch(() => enqueueSnackbar('Erro ao cancelar pagamento.', { variant: 'error' }))
    }

    const handleMarkPaid = () => {
        api.patch(`cashflow/payments/${id}/`, { status: 'paid' })
            .then((res) => {
                setPayment(res.data)
                enqueueSnackbar('Pagamento marcado como pago.', { variant: 'success' })
            })
            .catch(() => enqueueSnackbar('Erro ao atualizar pagamento.', { variant: 'error' }))
    }

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Skeleton variant="rounded" height={400} />
            </Container>
        )
    }

    if (!payment) {
        return (
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Typography>Pagamento não encontrado.</Typography>
            </Container>
        )
    }

    const statusInfo = STATUS_MAP[payment.computed_status] || STATUS_MAP.pending
    const valueCents = Math.round((payment.value || 0) * 100)

    return (
        <>
            <Helmet>
                <title>Pagamento #{payment.id} - Bloco B3</title>
            </Helmet>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="md" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                        <IconButton onClick={() => navigate('/financeiro/pagamentos')}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B', flex: 1 }}>
                            Pagamento #{payment.id}
                        </Typography>
                        <IconButton onClick={handleToggleFavorite}>
                            {payment.is_favorite ? <Star sx={{ color: '#F59E0B' }} /> : <StarBorder sx={{ color: '#94A3B8' }} />}
                        </IconButton>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Main Info */}
                        <Grid item xs={12} md={8}>
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                            Informações do Pagamento
                                        </Typography>
                                        <Chip
                                            label={statusInfo.label}
                                            size="small"
                                            sx={{
                                                bgcolor: `${statusInfo.color}14`,
                                                color: statusInfo.color,
                                                fontWeight: 600,
                                                border: `1px solid ${statusInfo.color}30`,
                                            }}
                                        />
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <Person sx={{ fontSize: 18, color: '#94A3B8' }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>Contato</Typography>
                                                    <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                        {payment.contact_detail?.name || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <CalendarMonth sx={{ fontSize: 18, color: '#94A3B8' }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>Vencimento</Typography>
                                                    <Typography variant="body2" sx={{ color: payment.computed_status === 'overdue' ? '#EF4444' : '#1E293B', fontWeight: 500 }}>
                                                        {payment.due_date ? moment(payment.due_date).format('DD/MM/YYYY') : '-'}
                                                        {payment.computed_status === 'overdue' && ' (vencido)'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        {payment.payment_date && (
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <CalendarMonth sx={{ fontSize: 18, color: '#94A3B8' }} />
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>Data de Pagamento</Typography>
                                                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                            {moment(payment.payment_date).format('DD/MM/YYYY')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <AccountBalance sx={{ fontSize: 18, color: '#94A3B8' }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>Conta</Typography>
                                                    <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                        {payment.account_detail?.nickname || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <CategoryIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>Categoria</Typography>
                                                    <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                        {payment.category_detail?.name || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {payment.description && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>Descrição</Typography>
                                            <Typography variant="body2" sx={{ color: '#1E293B' }}>{payment.description}</Typography>
                                        </Box>
                                    )}

                                    {payment.reference && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>Referência</Typography>
                                            <Typography variant="body2" sx={{ color: '#1E293B' }}>{payment.reference}</Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                            {Masks.money(String(valueCents))}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Notes */}
                            {payment.notes && (
                                <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', mt: 2 }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: 600, mb: 1 }}>
                                            Anotações
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1E293B', whiteSpace: 'pre-wrap' }}>
                                            {payment.notes}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>

                        {/* Actions */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: 600, mb: 2 }}>
                                        Ações
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {payment.status === 'pending' && (
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={handleMarkPaid}
                                                sx={{
                                                    bgcolor: '#22C55E',
                                                    '&:hover': { bgcolor: '#16A34A' },
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Marcar como Pago
                                            </Button>
                                        )}
                                        {payment.status !== 'canceled' && (
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={handleCancel}
                                                sx={{
                                                    textTransform: 'none',
                                                    color: '#EF4444',
                                                    borderColor: '#EF444430',
                                                    '&:hover': { borderColor: '#EF4444', bgcolor: '#FEF2F2' },
                                                }}
                                            >
                                                Cancelar Pagamento
                                            </Button>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Summary */}
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', mt: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: 600, mb: 2 }}>
                                        Resumo
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#64748B' }}>Valor</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                            {Masks.money(String(valueCents))}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.7rem' }}>
                                            Criado em {moment(payment.created_at).format('DD/MM/YYYY HH:mm')}
                                        </Typography>
                                    </Box>
                                    {payment.created_by_name && (
                                        <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.7rem' }}>
                                            Por {payment.created_by_name}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    )
}

export default PaymentsDetail
